/**
 * Centralized API Service for TECDocChart
 * 
 * Provides centralized API management with:
 * - Automatic loading state management
 * - JWT authentication handling
 * - CSRF token integration
 * - Error handling and retry logic
 */

import { useLoading } from '../contexts/LoadingContext';
import { useCallback } from 'react';

interface APIServiceConfig {
  baseURL?: string;
  timeout?: number;
  retries?: number;
}

interface APIResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
}

interface APIError {
  message: string;
  status: number;
  data?: any;
}

class APIServiceCore {
  private baseURL: string;
  private timeout: number;

  constructor(config: APIServiceConfig = {}) {
    this.baseURL = config.baseURL || '';
    this.timeout = config.timeout || 30000;
    // TODO: Implement retry logic when needed
    // this.retries = config.retries || 1;
  }

  private async getCSRFToken(): Promise<string> {
    // Try to get from global context first (Django template injection)
    const globalContext = (window as any).globalContext;
    if (globalContext?.authContext?.csrf_token) {
      return globalContext.authContext.csrf_token;
    }

    // Fallback: get from cookies
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrftoken') {
        return decodeURIComponent(value);
      }
    }

    throw new Error('CSRF token not found');
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add CSRF token for state-changing operations
    try {
      const csrfToken = await this.getCSRFToken();
      headers['X-CSRFToken'] = csrfToken;
    } catch (error) {
      console.warn('Could not retrieve CSRF token:', error);
    }

    // TODO: Add JWT token handling when available
    // const jwtToken = await this.getJWTToken();
    // if (jwtToken) {
    //   headers['Authorization'] = `Bearer ${jwtToken}`;
    // }

    return headers;
  }

  private async makeRequest<T>(
    url: string,
    options: RequestInit = {},
    loadingMessage?: string,
    loadingId?: string,
    showLoading?: (message?: string, id?: string) => void,
    hideLoading?: (id?: string) => void
  ): Promise<APIResponse<T>> {
    const fullURL = url.startsWith('http') ? url : `${this.baseURL}${url}`;
    const requestId = loadingId || `api-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Show loading if handlers provided
    if (showLoading) {
      showLoading(loadingMessage, requestId);
    }

    try {
      const authHeaders = await this.getAuthHeaders();
      
      const response = await fetch(fullURL, {
        ...options,
        credentials: 'include', // Include cookies for Django session
        headers: {
          ...authHeaders,
          ...options.headers,
        },
        signal: AbortSignal.timeout(this.timeout),
      });

      if (!response.ok) {
        // Only a 401 (unauthenticated) means the session is gone — redirect to login.
        // A 403 is Forbidden (missing permission, or a CSRF hiccup) for a user who IS logged in;
        // redirecting to login on 403 was logging active users out repeatedly. Genuine session
        // expiry also surfaces as Django's 302 -> login HTML, handled by the content-type check below.
        if (response.status === 401) {
          console.warn('Session expired or unauthorized. Redirecting to login...');
          const currentPath = window.location.pathname + window.location.search;
          const returnPath = encodeURIComponent(currentPath);
          window.location.href = `/login?next=${returnPath}`;
          // Return a never-resolving promise since we're redirecting
          return new Promise(() => {});
        }
        // Surface the server's own explanation when it sent one.
        //
        // Django's error helpers return {"message": "..."} with the 400/404 (see
        // json_bad_request_error_response in TECUtils.py). Throwing a bare
        // `HTTP 400: Bad Request` discarded that body, so validation failures reached the user as
        // a status code with no reason -- e.g. setting an ePrescribe GUID to "abc", or to a GUID
        // already held by another employee, both of which the server explains precisely.
        //
        // The body is read defensively: an error response is not guaranteed to be JSON (a proxy
        // or Django debug page may return HTML), and failing to parse it must not mask the
        // original HTTP failure.
        let errorData: any;
        let serverMessage = '';
        try {
          const errorText = await response.text();
          if (errorText) {
            try {
              errorData = JSON.parse(errorText);
              const candidate = errorData?.message ?? errorData?.error ?? errorData?.detail;
              if (typeof candidate === 'string' && candidate.trim()) {
                serverMessage = candidate.trim();
              }
            } catch {
              // Non-JSON body (HTML error page). Keep the status-based message.
            }
          }
        } catch {
          // Body already consumed or the stream failed; fall through to the status message.
        }

        throw new APIError(
          serverMessage || `HTTP ${response.status}: ${response.statusText}`,
          response.status,
          errorData,
        );
      }

      // Check if the response is actually JSON — Django may return an HTML page
      // (200 with text/html) via a redirect chain.
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        const text = await response.text();
        if (text.includes('<!DOCTYPE') || text.includes('<html')) {
          // Distinguish a PERMISSION DENIAL from a real session expiry. Django's
          // @app_permission_required redirects (302) an *authenticated* user who lacks a
          // permission to /permission_denied. That must NOT bounce to /login — doing so loops
          // forever for limited-permission users (e.g. a PSC opening an encounter chart, where
          // get_admin_settings requires patient_encounter). Throw so the caller's own error
          // handling can degrade gracefully (e.g. getAdminSettings returns null and the chart
          // still renders the parts the user is allowed to see).
          if ((response.url || '').includes('/permission_denied')) {
            throw new APIError('Permission denied', 403);
          }
          console.warn('Session expired (received HTML instead of JSON). Redirecting to login...');
          const currentPath = window.location.pathname + window.location.search;
          const returnPath = encodeURIComponent(currentPath);
          window.location.href = `/login?next=${returnPath}`;
          return new Promise(() => {});
        }
        throw new APIError(`Unexpected response type: ${contentType}`, response.status);
      }

      const data = await response.json();

      return {
        data,
        status: response.status,
        statusText: response.statusText,
      };
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }
      if (error instanceof Error) {
        // Catch JSON parse errors from expired sessions that slipped through
        if (error.message.includes('is not valid JSON') || error.message.includes('Unexpected token')) {
          console.warn('Session likely expired (JSON parse failed). Redirecting to login...');
          const currentPath = window.location.pathname + window.location.search;
          const returnPath = encodeURIComponent(currentPath);
          window.location.href = `/login?next=${returnPath}`;
          return new Promise(() => {});
        }
        throw new APIError(error.message, 0);
      }
      throw new APIError('Unknown API error', 0);
    } finally {
      // Hide loading if handlers provided
      if (hideLoading) {
        hideLoading(requestId);
      }
    }
  }

  // Core HTTP methods - used internally
  async get<T>(url: string, loadingMessage?: string, loadingId?: string, showLoading?: (message?: string, id?: string) => void, hideLoading?: (id?: string) => void): Promise<APIResponse<T>> {
    return this.makeRequest<T>(url, { method: 'GET' }, loadingMessage, loadingId, showLoading, hideLoading);
  }

  async post<T>(url: string, data?: any, loadingMessage?: string, loadingId?: string, showLoading?: (message?: string, id?: string) => void, hideLoading?: (id?: string) => void): Promise<APIResponse<T>> {
    return this.makeRequest<T>(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }, loadingMessage, loadingId, showLoading, hideLoading);
  }

  async put<T>(url: string, data?: any, loadingMessage?: string, loadingId?: string, showLoading?: (message?: string, id?: string) => void, hideLoading?: (id?: string) => void): Promise<APIResponse<T>> {
    return this.makeRequest<T>(url, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }, loadingMessage, loadingId, showLoading, hideLoading);
  }

  async delete<T>(url: string, loadingMessage?: string, loadingId?: string, showLoading?: (message?: string, id?: string) => void, hideLoading?: (id?: string) => void): Promise<APIResponse<T>> {
    return this.makeRequest<T>(url, { method: 'DELETE' }, loadingMessage, loadingId, showLoading, hideLoading);
  }
}

// Custom API error class
class APIError extends Error {
  status: number;
  data?: any;

  constructor(message: string, status: number, data?: any) {
    super(message);
    this.name = 'APIError';
    this.status = status;
    this.data = data;
  }
}

// Create singleton instance
const apiServiceCore = new APIServiceCore();

/**
 * Hook for using API service with automatic loading states
 */
export const useAPIService = () => {
  const { showLoading, hideLoading } = useLoading();

  const apiService = {
    get: useCallback(<T = any>(url: string, loadingMessage = 'Loading...'): Promise<APIResponse<T>> => {
      return apiServiceCore.get<T>(url, loadingMessage, undefined, showLoading, hideLoading);
    }, [showLoading, hideLoading]),

    post: useCallback(<T = any>(url: string, data?: any, loadingMessage = 'Saving...'): Promise<APIResponse<T>> => {
      return apiServiceCore.post<T>(url, data, loadingMessage, undefined, showLoading, hideLoading);
    }, [showLoading, hideLoading]),

    put: useCallback(<T = any>(url: string, data?: any, loadingMessage = 'Updating...'): Promise<APIResponse<T>> => {
      return apiServiceCore.put<T>(url, data, loadingMessage, undefined, showLoading, hideLoading);
    }, [showLoading, hideLoading]),

    delete: useCallback(<T = any>(url: string, loadingMessage = 'Deleting...'): Promise<APIResponse<T>> => {
      return apiServiceCore.delete<T>(url, loadingMessage, undefined, showLoading, hideLoading);
    }, [showLoading, hideLoading]),
  };

  return apiService;
};

/**
 * API service without automatic loading (for background operations)
 */
export const apiService = {
  get: <T = any>(url: string): Promise<APIResponse<T>> => 
    apiServiceCore.get<T>(url),
  
  post: <T = any>(url: string, data?: any): Promise<APIResponse<T>> => 
    apiServiceCore.post<T>(url, data),
    
  put: <T = any>(url: string, data?: any): Promise<APIResponse<T>> => 
    apiServiceCore.put<T>(url, data),
    
  delete: <T = any>(url: string): Promise<APIResponse<T>> => 
    apiServiceCore.delete<T>(url),
};

export { APIError };
export type { APIResponse };